// API Configuration
// This file handles API endpoint configuration for different environments

export const getApiBaseUrl = () => {
  if (import.meta.env?.VITE_EMAIL_MKT_API_BASE_URL) {
    const raw = import.meta.env.VITE_EMAIL_MKT_API_BASE_URL.trim().replace(/\/+$/, '');
    return raw.endsWith('/api') ? raw : `${raw}/api`;
  }

  // In production, use the same origin
  if (typeof window !== 'undefined' && window.location.origin.includes('://') && !window.location.hostname.includes('localhost')) {
    return `${window.location.origin}/api`;
  }
  
  // In development, check for environment variable first
  if (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL) {
    const raw = process.env.REACT_APP_API_URL.trim().replace(/\/+$/, '');
    return raw.endsWith('/api') ? raw : `${raw}/api`;
  }
  
  // Default development API URL
  return 'http://localhost:4000/api';
};

export const API_BASE = getApiBaseUrl();

// API endpoints
export const API_ENDPOINTS = {
  // Audience endpoints
  AUDIENCE: {
    CONTACTS: `${API_BASE}/audience/contacts`,
    COUNT: `${API_BASE}/audience/count`,
    PREVIEW: `${API_BASE}/audience/preview`,
    ALL: `${API_BASE}/audience/all`,
    TABLES: `${API_BASE}/audience/tables`,
    TABLE_FIELDS: (tableName) => `${API_BASE}/audience/tables/${tableName}/fields`,
    STATS: `${API_BASE}/audience/stats/overview`,
    TABLE_STATS: `${API_BASE}/audience/tables/stats`
  },
  
  // Saved segments endpoints
  SAVED_SEGMENTS: {
    BASE: `${API_BASE}/saved-segments`,
    BY_ID: (id) => `${API_BASE}/saved-segments/${id}`,
    CONTACTS: (id) => `${API_BASE}/saved-segments/${id}/contacts`,
    PREVIEW: `${API_BASE}/saved-segments/preview`,
    STATS: `${API_BASE}/saved-segments/stats/overview`,
    COMPANY_FIELD_OPTIONS: `${API_BASE}/saved-segments/company-field-options`
  }
};

// Helper function for making API requests
export const apiRequest = async (url, options = {}) => {
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    },
    credentials: 'include'
  };

  const finalOptions = { ...defaultOptions, ...options };

  try {
    const response = await fetch(url, finalOptions);
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

export default {
  API_BASE,
  API_ENDPOINTS,
  apiRequest
};

/**
 * WebSocket URL for the email-marketing backend.
 *
 * Derived from the same env var as the API base so it follows the backend wherever that points,
 * *including its path*. The backend is published under a sub-path (`/email-marketing`) and the
 * reverse proxy only tunnels upgrade requests on that exact path — connecting to the bare host
 * reaches the static frontend instead, which answers with HTML and fails the handshake.
 *
 * Unlike getApiBaseUrl() this does not append `/api`: the WebSocket server is attached to the
 * HTTP server itself, not mounted under the API router.
 */
export const getWebSocketUrl = () => {
  const envApiUrl = import.meta.env?.VITE_EMAIL_MKT_API_BASE_URL;

  if (envApiUrl) {
    try {
      const parsed = new URL(envApiUrl.trim());
      const wsProtocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
      // Strip a trailing slash and treat a bare '/' as no path: the proxy rule matches the path
      // exactly, and '/email-marketing/' is not '/email-marketing'.
      const pathname = parsed.pathname.replace(/\/+$/, '');
      return `${wsProtocol}//${parsed.host}${pathname}`;
    } catch {
      // Not a parseable absolute URL — fall back to swapping the scheme on the raw value.
      return envApiUrl.trim().replace(/^http:/i, 'ws:').replace(/^https:/i, 'wss:');
    }
  }

  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = import.meta.env?.PROD ? window.location.host : '127.0.0.1:4000';
  return `${wsProtocol}//${host}`;
};
